import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Input, Select, message } from 'antd';
import { registerUser, fetchBranches } from '../api';
import RoleSelect from '../../../components/UI/RoleSelect';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../constants/permissions';
import { ActionType, ModuleType, PermissionButton } from "components";

const { Option } = Select;

const MODULE_OPTIONS = [
  { label: "Billing", value: "Bill" },
  { label: "Invoice", value: "Invoice" },
  // ...add more as needed
];

const REGION_OPTIONS = [
  { label: "East", value: "East" },
  { label: "West", value: "West" },
  { label: "North", value: "North" },
  { label: "South", value: "South" },
  // ...add more as needed
];

const CreateUserPopup = ({ reloadUsers }) => {
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [branches, setBranches] = useState([]);
  const [form] = Form.useForm();
  const { can, canViewField, canEditField } = usePermissions();

  // Two-tier show/edit permission checks
  const canFieldNameShow = canEditField('user', 'name') || canViewField('user', 'name');
  const canFieldNameEdit = canEditField('user', 'name');
  const canFieldEmailShow = canEditField('user', 'email') || canViewField('user', 'email');
  const canFieldEmailEdit = canEditField('user', 'email');
  const canFieldAdharShow = canEditField('user', 'adhar') || canViewField('user', 'adhar');
  const canFieldAdharEdit = canEditField('user', 'adhar');
  const canFieldPanShow = canEditField('user', 'pan') || canViewField('user', 'pan');
  const canFieldPanEdit = canEditField('user', 'pan');
  const canFieldPasswordShow = canEditField('user', 'password') || canViewField('user', 'password');
  const canFieldPasswordEdit = canEditField('user', 'password');
  const canFieldRoleShow = canEditField('user', 'role') || canViewField('user', 'role');
  const canFieldRoleEdit = canEditField('user', 'role');
  const canFieldBranchShow = canEditField('user', 'branch') || canViewField('user', 'branch');
  const canFieldBranchEdit = canEditField('user', 'branch');
  const module = ModuleType.USER;

  useEffect(() => {
    loadBranches();
  }, []);

  const loadBranches = async () => {
    try {
      const response = await fetchBranches({});
      if (response.success) {
        setBranches(response.data);
      }
    } catch (error) {
      console.error('Error loading branches:', error);
    }
  };

  const handleCreate = async (values) => {
    setSubmitting(true);
    try {
      // Find the selected branch to get its name
      const selectedBranch = branches.find(branch => branch._id === values.branch);
      
      // Add branchName to the values
      const formData = {
        ...values,
        branchName: selectedBranch?.name || ''
      };
      
      const response = await registerUser(formData);
      message.success(response.message || 'User created successfully');
      setVisible(false);
      form.resetFields();
      if (reloadUsers) reloadUsers();
    } catch (err) {
      console.error('Create user error:', err);
      const msg = err?.response?.data?.message || 'Failed to create user';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PermissionButton
        buttonType={ActionType.CREATE}
        module={module}
        type="primary"
        onClick={() => setVisible(true)}
      >
        Add User
      </PermissionButton>

      <Modal
        title="Create New User"
        open={visible}
        onCancel={() => setVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={submitting}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          {canFieldNameShow && (
            <Form.Item name="name" label="Name" rules={[{ required: true }]}>
              <Input disabled={!canFieldNameEdit} />
            </Form.Item>
          )}
          {canFieldEmailShow && (
            <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
              <Input disabled={!canFieldEmailEdit} />
            </Form.Item>
          )}
          {canFieldAdharShow && (
            <Form.Item name="adhar" label="Aadhaar" rules={[{ required: true }]}>
              <Input disabled={!canFieldAdharEdit} />
            </Form.Item>
          )}
          {canFieldPanShow && (
            <Form.Item name="pan" label="PAN" rules={[{ required: true }]}>
              <Input disabled={!canFieldPanEdit} />
            </Form.Item>
          )}
          {canFieldPasswordShow && (
            <Form.Item name="password" label="Password" rules={[{ required: true }]}>
              <Input.Password disabled={!canFieldPasswordEdit} />
            </Form.Item>
          )}
          {canFieldRoleShow && (
            <Form.Item name="role" label="Role" rules={[{ required: true }]}>
              <RoleSelect allowCreate disabled={!canFieldRoleEdit} />
            </Form.Item>
          )}
          {canFieldBranchShow && (
            <Form.Item name="branch" label="Branch" rules={[{ required: true }]}>
              <Select placeholder="Select branch" disabled={!canFieldBranchEdit}>
                {branches.map((branch) => (
                  <Option key={branch._id} value={branch._id}>
                    {branch.name} ({branch.code})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item name="region" label="Region">
            <Select placeholder="Select region (optional)">
              {REGION_OPTIONS.map((r) => (
                <Option key={r.value} value={r.value}>{r.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="modules" label="Modules" rules={[{ required: true, type: 'array', min: 1 }]}>
            <Select mode="multiple" placeholder="Select modules">
              {MODULE_OPTIONS.map((m) => (
                <Option key={m.value} value={m.value}>{m.label}</Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CreateUserPopup;
